import axios from 'axios';

// Use environment variable, fallback to Render URL
const API_URL = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "https://smartballoon-1.onrender.com/api";

// Create axios instance with absolute URL
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors and transient failures
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const originalRequest = error.config;
    
    // On 503 (Service Unavailable), DON'T logout - it's a transient Supabase error
    if (status === 503) {
      console.warn('[API] Transient error (503) - database temporarily unavailable');
      // Let the calling code handle retry/display
      return Promise.reject(error);
    }
    
    // On 502 (Bad Gateway from backend), DON'T logout - it's infrastructure
    if (status === 502) {
      console.warn('[API] Infrastructure error (502) - gateway issue');
      return Promise.reject(error);
    }
    
    // On 401, try to refresh the token ONCE before logging out
    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      console.log('[API] Token expired, attempting refresh...');
      
      try {
        // Try to refresh the session via backend
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const response = await axios.post(`${API_URL}/auth/refresh`, {
            refresh_token: refreshToken
          });
          
          if (response.data?.token) {
            // Save new tokens
            localStorage.setItem('token', response.data.token);
            if (response.data.refresh_token) {
              localStorage.setItem('refresh_token', response.data.refresh_token);
            }
            
            // Retry original request with new token
            originalRequest.headers.Authorization = `Bearer ${response.data.token}`;
            console.log('[API] Token refreshed successfully');
            return api(originalRequest);
          }
        }
      } catch (refreshError) {
        console.warn('[API] Token refresh failed:', refreshError.message);
      }
      
      // If refresh fails, logout
      console.log('[API] Logging out due to auth failure');
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth endpoints
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  updateLanguage: (language) => api.put(`/auth/language?language=${language}`),
  refresh: (refreshToken) => api.post('/auth/refresh', { refresh_token: refreshToken }),
};

// Projects
export const projectsAPI = {
  list: () => api.get('/projects'),
  get: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => {
    // If data contains markers, save them separately
    if (data.markers) {
      return api.post(`/projects/${id}/markers`, data.markers);
    }
    // Otherwise update project metadata
    return api.put(`/projects/${id}`, data);
  },
  delete: (id) => api.delete(`/projects/${id}`),
  saveMarkers: (id, markers) => api.post(`/projects/${id}/markers`, markers),
  uploadDrawing: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    // Returns { success, filename, quality_warning, quality_details, final_size_mb, is_tiled, tiles }
    return api.post(`/projects/${id}/upload`, formData, {
      headers: { 'Content-Type': undefined },
      timeout: 180000  // 3 minute timeout for large files with tiling
    });
  },
  uploadLogo: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/projects/${id}/upload-logo`, formData, {
      headers: { 'Content-Type': undefined }
    });
  },
  // CMM Report API (legacy single file)
  uploadMeasurementReport: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/projects/${id}/upload-measurement-report`, formData, {
      headers: { 'Content-Type': undefined }
    });
  },
  deleteMeasurementReport: (id) => api.delete(`/projects/${id}/measurement-report`),
  getMeasurementReportStatus: (id) => api.get(`/projects/${id}/measurement-report`),
  parseCMMReport: (id) => api.post(`/projects/${id}/parse-cmm-report`),
  applyCMMMeasurements: (id, measurements) => api.post(`/projects/${id}/apply-cmm-measurements`, { measurements }),
  
  // CMM Report API (multiple files - up to 5)
  uploadCMMReportsMultiple: (id, files) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    return api.post(`/projects/${id}/upload-cmm-reports`, formData, {
      headers: { 'Content-Type': undefined }
    });
  },
  getCMMReportsList: (id) => api.get(`/projects/${id}/cmm-reports`),
  deleteSingleCMMReport: (id, reportId) => api.delete(`/projects/${id}/cmm-reports/${reportId}`),
  parseCMMReportsMultiple: (id, mode = 'duplicate', assignments = null) => api.post(`/projects/${id}/parse-cmm-reports-multiple`, { mode, assignments }),
  applyCMMMeasurementsMultiSample: (id, measurements) => api.post(`/projects/${id}/apply-cmm-measurements-multi-sample`, { measurements }),
  
  // Tiles API for large drawings
  getTiles: (id) => api.get(`/projects/${id}/tiles`),
  getTileImage: (id, tileIndex) => api.get(`/projects/${id}/tiles/${tileIndex}`),
  renameTile: (id, tileIndex, name) => api.put(`/projects/${id}/tiles/${tileIndex}/rename`, { name }),
  
  exportPDF: (id) => api.get(`/projects/${id}/export/pdf`, { responseType: 'blob' }),
  exportExcel: (id, lang = 'it') => api.get(`/projects/${id}/export/excel?lang=${lang}`, { responseType: 'blob' }),
  aiDetect: (id) => api.post(`/projects/${id}/ai-detect`),  // Returns job_id for polling
  aiDetectStatus: (id, jobId) => api.get(`/projects/${id}/ai-detect/status/${jobId}`),  // Poll for results
  readDimension: (id, x, y) => api.post(`/projects/${id}/read-dimension`, { x, y }),
  previewDimension: (id, x, y) => api.post(`/projects/${id}/preview-dimension`, { x, y }),
  analyzeTolerances: (id) => api.post(`/projects/${id}/analyze-tolerances`),
  setToleranceClass: (id, toleranceClass, manualValue = null) => api.post(`/projects/${id}/set-tolerance-class`, { 
    tolerance_class: toleranceClass, 
    manual_value: manualValue 
  }),
  moveToFolder: (id, folderId) => api.put(`/projects/${id}/move-to-folder`, { folder_id: folderId }),
};

// User Settings
export const userAPI = {
  uploadCompanyLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/user/upload-company-logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getCompanyLogo: () => api.get('/user/company-logo'),
  deleteCompanyLogo: () => api.delete('/user/company-logo'),
};

// Folders (Project Organization)
export const foldersAPI = {
  list: (folderType = null) => api.get('/folders', { params: folderType ? { folder_type: folderType } : {} }),
  create: (name, folderType = 'projects', parentId = null, color = null) => 
    api.post('/folders', { name, folder_type: folderType, parent_id: parentId, color }),
  update: (id, data) => api.put(`/folders/${id}`, data),
  delete: (id) => api.delete(`/folders/${id}`),
};

// Subscriptions
export const subscriptionAPI = {
  getPlans: () => api.get('/plans'),
  checkout: (plan, originUrl) => api.post('/payments/checkout', { plan, origin_url: originUrl }),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  customerPortal: () => api.post('/customer-portal'),
};

// Comparisons (Drawing Comparison)
export const comparisonsAPI = {
  list: (folderId = null) => api.get('/comparisons', { params: folderId ? { folder_id: folderId } : {} }),
  get: (id) => api.get(`/comparisons/${id}`),
  create: (data) => api.post('/comparisons', data),
  delete: (id) => api.delete(`/comparisons/${id}`),
  moveToFolder: (id, folderId) => api.patch(`/comparisons/${id}/folder`, { folder_id: folderId }),
  uploadDrawing: (id, side, file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('side', side);  // 'A' or 'B'
    return api.post(`/comparisons/${id}/upload-drawing`, formData, {
      headers: { 'Content-Type': undefined },
      timeout: 120000
    });
  },
  analyze: (id, options = {}) => api.post(`/comparisons/${id}/analyze`, options, { timeout: 180000 }),
  exportPDF: (id, modifiedMarkers = null, modifiedNotes = null) => {
    if ((modifiedMarkers && modifiedMarkers.length > 0) || (modifiedNotes && modifiedNotes.length > 0)) {
      // POST with modified markers and notes
      return api.post(`/comparisons/${id}/export/pdf`, { 
        markers: modifiedMarkers || [], 
        notes: modifiedNotes || [] 
      }, { responseType: 'blob' });
    }
    // GET for original markers
    return api.get(`/comparisons/${id}/export/pdf`, { responseType: 'blob' });
  },
  updateMarkers: (id, markers, notes = []) => api.patch(`/comparisons/${id}/markers`, { markers, notes }),
  getFullOverlay: (id) => api.get(`/comparisons/${id}/overlay`),  // Lazy load full resolution overlay
  updateDrawing: (id, drawing, base64Data) => api.patch(`/comparisons/${id}/drawing/${drawing}`, { base64: base64Data }),
  analyzeGuidedViews: (id, views, resizeToMatch = false) => api.post(`/comparisons/${id}/analyze-guided`, { views, resize_to_match: resizeToMatch }),
  saveGuidedViews: (id, views) => api.post(`/comparisons/${id}/save-guided-views`, { views }),
  getGuidedViews: (id) => api.get(`/comparisons/${id}/guided-views`),
};

// AI Usage
export const aiAPI = {
  getUsage: () => api.get('/ai-usage'),
  // NEW: View-by-View Detection
  detectViews: (projectId) => api.post(`/projects/${projectId}/detect-views`),
  scanView: (projectId, viewIndex) => api.post(`/projects/${projectId}/scan-view/${viewIndex}`),
  // Manual custom area scanning
  scanCustomArea: (projectId, area) => api.post(`/projects/${projectId}/scan-custom-area`, area),
  // Claude Multi-Pass detection (ASYNC JOB-BASED, same 3x3 grid as GPT-4o)
  startClaudeMultipass: (projectId, options = {}) => api.post(`/projects/${projectId}/claude-multipass-start`, options),
  getClaudeMultipassStatus: (projectId, jobId) => api.get(`/projects/${projectId}/claude-multipass-status/${jobId}`),
  // GPT-4o Multi-Pass detection (ASYNC JOB-BASED)
  startMultipass: (projectId, options = {}) => api.post(`/projects/${projectId}/multipass-start`, options),
  getMultipassStatus: (projectId, jobId) => api.get(`/projects/${projectId}/multipass-status/${jobId}`),
  // Legacy endpoints
  detectAll: (projectId) => api.post(`/projects/${projectId}/detect-all`, {}, { timeout: 180000 }),
  detectAllTiled: (projectId, options = {}) => api.post(`/projects/${projectId}/detect-all-tiled`, options, { timeout: 300000 }),
  // EXPERIMENTAL: Vector PDF detection
  getVectorInfo: (projectId) => api.get(`/projects/${projectId}/vector-info`),
  detectVector: (projectId) => api.post(`/projects/${projectId}/detect-vector`, {}, { timeout: 60000 }),
  checkVector: (projectId) => api.post(`/projects/${projectId}/check-vector`),
};

export default api;
